/**
 * Edit Renderer Library
 * Feature: 006-critique-editing
 *
 * Reusable TypeScript library for dynamically applying edit graphs to images.
 * This library makes it trivial to apply an edit graph to a thumbnail or full-screen
 * image anywhere in the system.
 *
 * Design Principles:
 * 1. Pure Functions - No side effects, deterministic output
 * 2. Type-Safe - Full TypeScript coverage with strict types
 * 3. Composable - Edits compose multiplicatively (crop within crop, etc.)
 * 4. Performance - Optimized for <10ms execution
 * 5. Extensible - Designed to support future edit types (exposure, color, filters)
 * 6. Portable - Can be used in thumbnails, lightbox, critique feed, anywhere
 */

import type { CritiqueEdit, CropData, ComposedCrop, EditStyles } from '../types/edit.types'

/**
 * Composes an edit chain into a single ComposedCrop
 *
 * Edits are composed multiplicatively - each crop is applied as a percentage
 * of the previous crop region. This allows for chained refinement where
 * critics can crop a previous crop suggestion.
 *
 * @param editChain - Array of edits from root to leaf (ordered by depth)
 * @returns Final crop coordinates (percentage of original image)
 *
 * @example
 * // Edit A: Crop to center 50%
 * const editA = { editData: { x: 25, y: 25, width: 50, height: 50 } }
 * // Edit B: Crop Edit A's top-left quadrant (50% of Edit A)
 * const editB = { editData: { x: 0, y: 0, width: 50, height: 50 } }
 * // Result: { x: 25, y: 25, width: 25, height: 25 } (25% of original, top-left of Edit A)
 * const composed = composeEditChain([editA, editB])
 */
export function composeEditChain(editChain: CritiqueEdit[]): ComposedCrop {
  // Start with full image
  let absoluteCrop: ComposedCrop = { x: 0, y: 0, width: 100, height: 100 }

  for (const edit of editChain) {
    if (edit.editType === 'crop') {
      const cropData = edit.editData as CropData

      // Compose crops multiplicatively
      // Each crop is a percentage of the previous crop region
      absoluteCrop = {
        x: absoluteCrop.x + (absoluteCrop.width * cropData.x) / 100,
        y: absoluteCrop.y + (absoluteCrop.height * cropData.y) / 100,
        width: (absoluteCrop.width * cropData.width) / 100,
        height: (absoluteCrop.height * cropData.height) / 100,
      }
    }
    // Future: handle 'exposure', 'color', etc.
  }

  return absoluteCrop
}

/**
 * Converts a ComposedCrop to CSS clip-path inset values
 *
 * CSS clip-path inset() takes four values: top, right, bottom, left
 * representing the distances from each edge to clip.
 *
 * @param crop - Composed crop coordinates (percentage of original image)
 * @returns CSS clip-path string
 *
 * @example
 * // Crop to center 50x50%
 * cropToClipPath({ x: 25, y: 25, width: 50, height: 50 })
 * // Returns: "inset(25% 25% 25% 25%)"
 */
export function cropToClipPath(crop: ComposedCrop): string {
  // Calculate inset distances from each edge
  const top = crop.y
  const right = 100 - crop.x - crop.width
  const bottom = 100 - crop.y - crop.height
  const left = crop.x

  return `inset(${top}% ${right}% ${bottom}% ${left}%)`
}

/**
 * Generates CSS styles for an edit chain
 *
 * This is the primary function for applying edits to images. It composes
 * the edit chain and returns CSS properties that can be directly applied
 * to an <img> element.
 *
 * @param editChain - Array of edits from root to leaf
 * @returns CSS properties object for applying to img element
 *
 * @example
 * const styles = generateEditStyles(editChain)
 * <img src={photo.url} style={styles} />
 */
export function generateEditStyles(editChain: CritiqueEdit[]): EditStyles {
  const composedCrop = composeEditChain(editChain)
  const clipPath = cropToClipPath(composedCrop)

  return {
    clipPath,
    WebkitClipPath: clipPath, // Browser compatibility (Safari)
    // Future: add filter, transform properties for exposure/color/rotation
  }
}

/**
 * Convenience function: apply a single CropData to an image
 *
 * This is useful when you have a single crop (not a chain) and want
 * to quickly generate the styles.
 *
 * @param crop - Single crop to apply
 * @returns CSS properties object
 *
 * @example
 * const styles = applyCrop({ x: 10, y: 10, width: 80, height: 80, aspectRatio: 'free' })
 * <img src={photo.url} style={styles} />
 */
export function applyCrop(crop: CropData): EditStyles {
  // Create a temporary CritiqueEdit for composition
  const tempEdit: CritiqueEdit = {
    id: 'temp',
    critiqueId: 'temp',
    editType: 'crop',
    editData: crop,
    baseEditId: null,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  }

  return generateEditStyles([tempEdit])
}

/**
 * Calculate composed crop for display purposes (without generating CSS)
 *
 * Useful for showing crop coordinates in UI or debugging.
 *
 * @param editChain - Array of edits from root to leaf
 * @returns Final crop coordinates as percentages
 *
 * @example
 * const crop = getComposedCrop(editChain)
 * console.log(`Final crop: ${crop.width}% x ${crop.height}%`)
 */
export function getComposedCrop(editChain: CritiqueEdit[]): ComposedCrop {
  return composeEditChain(editChain)
}
